import React from 'react';
import PropTypes from 'prop-types';
import CoachingPage from './CoachingPage';
import loadDataForReview from 'actions/coaching/loadDataForReview';
import AuthStore from 'stores/AuthStore';
import CallStore from 'stores/files/CallStore';
import ScorecardStore from 'stores/ScorecardStore';
import UserStore from 'stores/UserStore';
import CoachingPlanStore from 'stores/coaching/CoachingPlanStore';
import CoachingReviewStore from 'stores/coaching/CoachingReviewStore';
import CoachingReviewTypeStore from 'stores/coaching/CoachingReviewTypeStore';
import CoachingPlanActions from 'actions/coaching/CoachingPlanActions';
import ScorecardActions from 'actions/scorecards/ScorecardActions';
import UserActions from 'actions/UserActions';
import CoachingReviewTypeActions from 'actions/coaching/CoachingReviewTypeActions';
import './CoachingPage.styl';
import DirectReportsActions from 'actions/coaching/DirectReportsActions';
import connectToStores from 'alt-utils/lib/connectToStores';
import * as queryParams from 'utils/queryParams';
import {List, Map} from 'immutable';
import isString from 'lodash/isString';
import CoachingStatsActions from 'actions/coaching/CoachingStatsActions';
import CoachingReviewTypeSource from 'sources/coaching/CoachingReviewTypeSource';
import {associateBy} from 'utils/associateBy';
import CardReasonActions from 'actions/CardReasonActions';
import CoachingPlaylistActions from 'actions/coaching/CoachingPlaylistActions';
import fetchMissingUsers from 'utils/fetchMissingUsers';
import CoachingPlaylistStore, {PLAYLIST_VISIBILITY} from 'stores/CoachingPlaylistStore';
import {getQueryParam} from 'utils/getQueryParam';
import SkillActions from 'actions/SkillActions';

@connectToStores
export default class CoachingPageContainer extends React.PureComponent {
  constructor(props) {
    super(props);
    this.state = {
      reviewTypes: Map(),
    };
    this.onFinishReview = this.onFinishReview.bind(this);
  }

  static getStores() {
    return [
      CoachingPlanStore,
      UserStore,
      ScorecardStore,
      CoachingReviewStore,
      CoachingReviewTypeStore,
      CallStore,
      AuthStore,
      CoachingPlaylistStore,
    ];
  }

  // TODO
  // Note that the actual props are surfaced from CoachingPlansWidgetContainer
  // That whole shim can probably go away at this point, and this component should 100%
  // own deriving these props.
  // If you're doing any heavy lifting in this component please refactor

  static planId(propPlanId, location) {
    const planIdQueryParam = getQueryParam(location, 'planId');
    const queryPlanId =
      typeof planIdQueryParam === 'string' ? parseInt(planIdQueryParam, 10) : undefined;

    const propPlanIdParsed =
      propPlanId && isString(propPlanId) ? parseInt(propPlanId, 10) : propPlanId;

    return propPlanIdParsed || queryPlanId;
  }

  static repId(repId, location) {
    if (repId) return repId;

    if (!location || !location.search) return undefined;

    return getQueryParam(location, 'repId') || getQueryParam(location, 'userId');
  }

  static reviewId(reviewId, location) {
    if (reviewId) return reviewId;

    if (!location || !location.search) return undefined;

    const reviewIdQueryParam = getQueryParam(location, 'reviewId');
    return typeof reviewIdQueryParam === 'string' ? parseInt(reviewIdQueryParam, 10) : undefined;
  }

  static getPropsFromStores({
    reviewId: propReviewId,
    planId: propPlanId,
    repId: propRepId,
    location,
  }) {
    const planId = this.planId(propPlanId, location);
    const repId = this.repId(propRepId, location);
    const reviewId = this.reviewId(propReviewId, location);

    const planStore = CoachingPlanStore.getState();
    const rep = UserStore.getState().users.get(repId);

    // TODO do we always have a plan id now? if so, remove this code
    const plan = planId
      ? planStore.planMap.get(planId)
      : (planStore.coachingPlans || List())
          .filter(p => p.recipientId === repId)
          .filter(
            plan =>
              !plan.completed || (plan.reviews && plan.reviews.some(r => r.status === 'COMPLETED'))
          )
          .sort((l, r) => r.created - l.created)
          .first();

    if (!plan) {
      return {
        planId,
        reviewId,
        repId,
      };
    }

    const review =
      plan.reviews.find(r => r.id === reviewId) ||
      (plan.completed
        ? plan.reviews
            .filter(r => r.status === 'COMPLETED')
            .sort((l, r) => l.scheduleAt - r.scheduleAt)
            .first() || plan.getNextReview(true)
        : plan.getNextReview(true));

    const users = UserStore.getState().users;
    const partiallyHydratedCalls = CallStore.getState().partiallyHydratedCalls;
    const sessionUser = AuthStore.getState().user;
    const coachingReviewTypes = CoachingReviewTypeStore.getState().types.filter((_, id) =>
      plan.reviewTypeIds.has(id)
    );

    const reviewStore = CoachingReviewStore.getState();
    const answers = reviewStore.coachingReviewAnswers.get(review && review.id, List());
    const reviewFileUploads = reviewStore.reviewFileUploads.get(review && review.id);

    const highLevelScores = reviewStore.highLevelScores.get(review && review.id);

    const isReviewFeedbackLoading = reviewStore.isReviewFeedbackLoading.get(review && review.id);

    const playlistVisible =
      CoachingPlaylistStore.getState().visiblity !== PLAYLIST_VISIBILITY.hidden;

    return {
      plan,
      rep,
      planId,
      reviewId,
      repId,
      review: review && review.set('reviewTypeAnswers', answers),
      users,
      partiallyHydratedCalls,
      sessionUser,
      isReviewFeedbackLoading,
      reviewFileUploads,
      highLevelScores,
      coachingReviewTypes,
      playlistVisible,
    };
  }

  componentDidMount() {
    const {repId, rep, planId, review, coachingReviewTypes} = this.props;

    setTimeout(async () => {
      UserActions.fetchAll({ids: [repId]});
      CoachingPlanActions.fetchAll({
        recipientId: repId,
        includeReviews: true,
        includeCompleted: true,
      });
      CoachingReviewTypeActions.fetchAllReviewTypes();
      DirectReportsActions.planVelocities([planId]);

      const plan = await CoachingPlanActions.retrieve(planId, {
        includeReviews: true,
        includeCompleted: true,
      });

      if (plan.scorecardId) {
        ScorecardActions.fetchScorecard(plan.scorecardId);
        CardReasonActions.fetchReasonSchemesForScorecard(plan.scorecardId);
      }
    }, 0);

    if (review) {
      setTimeout(async () => {
        // alt
        const missing = await fetchMissingReviewType(review, coachingReviewTypes);
        this.setState({
          reviewTypes: coachingReviewTypes.merge(missing),
        });
        loadDataForReview(review, CallStore.getState()).catch(console.error);
      }, 0);
    }

    // TODO this should be more targeted, but we don't have external admin skill curation
    // tools right now, so this wont cause issues now, but could in the future.
    if (rep && rep.teamId) {
      SkillActions.fetchAllByTeam.defer({team_id: rep.teamId});
    }
  }

  componentWillUnmount() {
    const {playlistVisible, review, sessionUser} = this.props;
    const isCoach = review ? review.coachUserId === sessionUser.id : false;
    // If this is a rep just hide the assistant.
    if (!isCoach) {
      return CoachingPlaylistActions.hide.defer();
    }

    // Is the playlist currently visible, change it to add call mode.
    if (playlistVisible) {
      CoachingPlaylistActions.setAddCallsMode.defer({keepState: true});
    }
  }

  componentDidUpdate(prevProps, prevState, snapshot) {
    const {rep, review, coachingReviewTypes, reviewFileUploads, users} = this.props;

    const currPlanId = CoachingPageContainer.planId(this.props.planId, this.props.location);

    const prevPlanId = CoachingPageContainer.planId(prevProps.planId, prevProps.location);

    if (currPlanId !== prevPlanId) {
      CoachingPlanActions.retrieve(currPlanId, {
        includeReviews: true,
        includeCompleted: true,
      }).then(plan => {
        if (plan.scorecardId) {
          ScorecardActions.fetchScorecard.defer(plan.scorecardId);
          CardReasonActions.fetchReasonSchemesForScorecard(plan.scorecardId);
        }
      });
      DirectReportsActions.planVelocities([currPlanId]);
    }

    if (review && !review.equals(prevProps.review)) {
      // wonderful defer workaround
      setTimeout(async () => {
        const missing = await fetchMissingReviewType(review, coachingReviewTypes);
        this.setState({
          reviewTypes: coachingReviewTypes.merge(missing),
        });
        loadDataForReview(review, CallStore.getState()).catch(console.error);
        if (!users.has(review.coachUserId)) {
          UserActions.fetchAll({ids: [review.coachUserId]});
        }
      }, 0);
    }

    if (rep && rep.teamId && !rep.equals(prevProps.rep)) {
      SkillActions.fetchAllByTeam.defer({team_id: rep.teamId});
    }

    // Fetch users who added calls in case they're from outside the rep's team
    if (reviewFileUploads && !reviewFileUploads.equals(prevProps.reviewFileUploads)) {
      const addedByIds = reviewFileUploads.flatMap(r => r.addedByUserId).toSet();
      setTimeout(() => fetchMissingUsers(addedByIds), 0);
    }
  }

  onSelectPlan(planId) {
    queryParams.set(
      {
        planId: planId,
        reviewId: null,
        contentDetailId: null,
      },
      true
    );
  }

  /**
   * Since reviews come nested under plans, after a review is complete,
   * we need to re-grab the plan and review dependent info.
   * @param review
   * @returns {Promise<void>}
   */
  onFinishReview(review) {
    CoachingPlanActions.retrieve(review.planId, {
      includeReviews: true,
      includeCompleted: true,
    }).then(() => {
      CoachingStatsActions.coachingStats.defer(review.planId);

      if (this.props.refreshUpcomingReviews) {
        this.props.refreshUpcomingReviews();
      } else {
        console.error("Don't know how to refresh upcoming reviews, this will lead to bugs!");
      }

      queryParams.set(
        {
          reviewId: review.id,
          editFeedback: null,
          autoStartReview: null,
        },
        true
      );
    });
  }

  render() {
    const {query} = this.props.location;
    const {
      adminId,
      managerId,
      coachId,
      planId,
      plan,
      review,
      repId,
      rep,
      partiallyHydratedCalls,
      users,
      sessionUser,
      isReviewFeedbackLoading,
      reviewFileUploads,
      highLevelScores,
    } = this.props;

    const {reviewTypes} = this.state;

    const contentDetailId = query.contentDetailId || (plan && plan.contentDetails.getIn([0, 'id']));

    if (!review || !plan || reviewTypes.size === 0) return null;

    const isCoach = review.coachUserId === sessionUser.id;
    const isRecipient = plan.recipient.id === sessionUser.id;

    const canEdit = isCoach || isRecipient;

    const modify = !!query.modify && canEdit;
    // CoachingPage enforces that only the review coach and recipient can edit a review.
    const editFeedback = !!query.editFeedback;
    const autoStartReview = !!query.autoStartReview && canEdit;

    return (
      <CoachingPage
        key={review.id + '|' + plan.id}
        onSelectContentDetail={id => queryParams.set({contentDetailId: id}, true)}
        onSelectPlan={this.onSelectPlan}
        onFinishReview={this.onFinishReview}
        adminId={adminId}
        managerId={managerId}
        coachId={coachId}
        repId={repId}
        planId={planId}
        contentDetailId={parseInt(contentDetailId, 10)}
        rep={rep}
        plan={plan}
        review={review}
        partiallyHydratedCalls={partiallyHydratedCalls}
        users={users}
        sessionUser={sessionUser}
        isReviewFeedbackLoading={isReviewFeedbackLoading}
        reviewFileUploads={reviewFileUploads}
        highLevelScores={highLevelScores}
        coachingReviewTypes={reviewTypes}
        autoStartReview={autoStartReview}
        // Triggers a modify dialog of the currently viewed review
        showModifyDialog={modify}
        // Displays edit UI for review feedback
        editFeedback={editFeedback}
        isCoach={isCoach}
        isRecipient={isRecipient}
      />
    );
  }
}

CoachingPageContainer.propTypes = {
  repId: PropTypes.string,
  planId: PropTypes.number.isRequired,
  reviewId: PropTypes.number,
  location: PropTypes.object, // React Router location
};

/**
 * Fetch the review type of the current review if it's missing (presumably because it's inactive
 * and therefore wasn't fetched with the list of all review types in the account, although,
 * annoyingly, this can also be triggered by timing issues, when the review types aren't yet
 * loaded).
 *
 * @param review the review
 * @param reviewTypes {Map<number, CoachingReviewType>} map of review type id to review type.
 * @return {Promise<*|Map<number, CoachingReviewType>>} map of missing review type id to missing
 * review type.
 */
async function fetchMissingReviewType(review, reviewTypes) {
  const storedReviewTypeIds = reviewTypes.map(type => type.id).toSet();
  if (review && review.reviewTypeId && !storedReviewTypeIds.has(review.reviewTypeId)) {
    const reviewTypes = await CoachingReviewTypeSource.fetchByIds(review.reviewTypeId);
    return associateBy(List(reviewTypes), rt => rt.id);
  }
  return Map();
}
